/**
 * Bill Memory on the Stock In screen (design 2026-10-21). The server is the authority on every
 * switch: it ignores these fields while Bill Memory is Off, so they are always sent.
 */
import http from '../services/httpClient';
import { contextFromPayload, decideLine, typedLineOf, ladderLineOf, reasonText, agreedVendor, makeMatchName, stripItemCode, shortDate } from './core';

/** 'green' → 'exact', 'yellow' → 'fuzzy', 'red' → 'none' (today's matchStatus words). */
export function statusFromColour(colour) {
  return { green: 'exact', yellow: 'fuzzy', red: 'none', left_out: 'fuzzy' }[colour] || null;
}

/**
 * What a card shows for one answer: today's colour class, one grey reason line, grey notes, and
 * the D17 quantity warning (shown small under the quantity input). Plain words only.
 */
export function deriveRowView(decision) {
  const notes = [];
  let qtyNote = null;
  for (const n of decision.notes || []) {
    const text = reasonText(n.code, n.params);
    if (!text) continue;
    if (n.code === 'qty_memory') qtyNote = text;
    else notes.push(text);
  }
  return {
    statusClass: statusFromColour(decision.colour),
    reasonText: decision.reason ? reasonText(decision.reason.code, decision.reason.params) : '',
    notes,
    qtyNote,
    chips: decision.chips || [],
    preselect: decision.preselect == null ? null : decision.preselect,
  };
}

/** The view of a form row, from the bm_* fields the scan or the typed ladder left on it; null for a row Bill Memory never answered. */
export function rowView(row) {
  if (!row || !row.bm_colour) return null;
  return deriveRowView({ colour: row.bm_colour, reason: row.bm_reason || null, notes: row.bm_notes || [],
    chips: row.smmf_suggestions || [], preselect: row.bm_preselect });
}

/** A name counts (the tap counter, the red card, the submit checks) once it has 3 letters or more. */
const nameCounts = (r) => String((r && r.item_name) || '').trim().length > 2;

/** A row still needs a tap: yellow, red without a red-card choice, or pending — unless the user already chose. */
export function rowNeedsTap(row) {
  if (!row || row.bm_settled || row.userConfirmed) return false;
  if (row.matchStatus === 'exact') return false;
  if (row.matchStatus === 'none') return !row.redClassified;
  return true;
}

/**
 * Fix 13 (Task 11) row 2: the row shows the red card's choices — every row counted as needing a tap offers
 * one: red (no item), a red answer the user asked to change, and a row filled from the vendor's history
 * (autofill) with no item.
 */
export function bmRedCardDue(row) {
  if (!row || !nameCounts(row) || row.redClassified) return false;
  const status = row.matchStatus || row.match_type || 'pending';
  if (row.userConfirmed && status !== 'none') return false;
  return status === 'none' || !!row.bmShowRedCard || (status === 'pending' && (row.item_id == null || row.item_id === ''));
}

/** The counter above the cards and behind the Save button. Rows whose name has fewer than 3 letters are not counted. */
export function countNeedTap(rows) {
  let total = 0;
  let need = 0;
  let firstIndex = null;
  (rows || []).forEach((r, i) => {
    if (!r || !nameCounts(r)) return;
    total += 1;
    if (rowNeedsTap(r)) {
      need += 1;
      if (firstIndex === null) firstIndex = i;
    }
  });
  return { ready: total - need, total, need, firstIndex };
}

/** "30 of 33 ready, 3 need a tap" — nothing when no row needs a tap. */
export function headerText(counts) {
  return counts && counts.need > 0 ? reasonText('ready', counts) : '';
}

/**
 * The ladder's context for typed rows (GET /api/bill-memory/context). vendorId only for a vendor
 * picked on the form (a certain vendor). → { switches, ctx } (ctx null while Off), or null when it
 * cannot load — the form then uses today's matching.
 */
export async function loadContext(vendorId) {
  try {
    const p = await http.get(`/bill-memory/context${vendorId ? `?vendor_id=${encodeURIComponent(vendorId)}` : ''}`);
    if (!p || !p.success || !p.switches) return null;
    return { switches: p.switches, ctx: contextFromPayload(p) };
  } catch (_) {
    return null;
  }
}

/**
 * What the form reads from a scan's Bill Memory record. BillScanner hands one record per uploaded
 * file ({ mode, screen, perFile }); a stock-in has one vendor field, so the vendor is the one
 * certain vendor the files agree on (core.agreedVendor, the server's rule at Save), else not
 * certain with every file's likely vendors as buttons (at most 3). left_out is every file's;
 * unsureScanRefs are the files with no certain vendor of their own (a vendor tap teaches their clues).
 */
export function scanBillMemory(bm) {
  if (!bm) return null;
  const files = (Array.isArray(bm.perFile) ? bm.perFile : [bm]).filter(Boolean);
  const vendors = files.map((f) => f.vendor || null);
  let vendor = agreedVendor(vendors);
  if (!vendor && vendors.some(Boolean)) {
    const seen = new Set();
    const suggestions = [];
    const offer = (s) => {
      if (!s || !s.vendor_id || seen.has(Number(s.vendor_id)) || suggestions.length >= 3) return;
      seen.add(Number(s.vendor_id));
      suggestions.push(s);
    };
    vendors.forEach((v) => { if (v && v.certain) offer({ vendor_id: v.vendor_id, name: v.name, why: v.clue }); });
    vendors.forEach((v) => ((v && v.suggestions) || []).forEach(offer));
    const noted = vendors.find((v) => v && v.gstin_note);
    vendor = { certain: false, vendor_id: null, name: null, vendor_ids: [], clue: null, suggestions, gstin_note: noted ? noted.gstin_note : null };
  }
  return {
    mode: bm.mode,
    screen: bm.screen,
    vendor,
    left_out: files.flatMap((f) => (Array.isArray(f.left_out) ? f.left_out : [])),
    unsureScanRefs: files.filter((f) => f.scan_ref && !(f.vendor && f.vendor.certain)).map((f) => f.scan_ref),
  };
}

/**
 * Settings › Inventory › Bill Memory is listed when this user may read it and Bill Memory is not
 * Off for the business — Shadow (every business's default) included, owner 23 Oct 2026 — or for
 * an Invexis admin in any mode. `access` is GET /bill-memory/access.
 */
export function bmSettingsVisible(access) {
  if (!access || !access.success || !access.canView) return false;
  return (!!access.mode && access.mode !== 'off') || !!access.isAdmin;
}

/**
 * The vendor the screen asks the context for — the one the server will use at Save (saveHook):
 * the vendor picked from the dropdown or the vendor buttons, else the scan's certain vendor
 * (a GSTIN or phone on the bill, design §9). An AI-filled vendor name is never certain.
 */
export function contextVendorId({ vendorCertain, selectedVendorId, scanVendor } = {}) {
  if (vendorCertain && Number(selectedVendorId) > 0) return Number(selectedVendorId);
  if (scanVendor && scanVendor.certain && Number(scanVendor.vendor_id) > 0) return Number(scanVendor.vendor_id);
  return null;
}

const STATUS = { green: 'exact', yellow: 'fuzzy', red: 'none', left_out: 'fuzzy' };

/**
 * The ladder's answer in the shape the form already reads from today's matcher:
 * { status: 'exact'|'fuzzy'|'none', itemId, inventoryItem, suggestions, preselect }.
 */
export function matchResultOf(decision, bm) {
  const inv = bm && bm.ctx ? bm.ctx.invIndex : null;
  const entry = (id) => (inv && id != null ? inv.get(id) : null) || null;
  const green = decision.colour === 'green' && decision.item_id != null;
  const e = green ? entry(decision.item_id) : null;
  return {
    status: STATUS[decision.colour] || 'none',
    itemId: green ? decision.item_id : null,
    inventoryItem: e ? { item_id: e.item_id, name: e.name, category: e.category_name || '', tax_slab: e.tax_slab, unit_symbol: e.unit_symbol || '' } : null,
    preselect: decision.preselect == null ? null : decision.preselect,
    suggestions: (decision.chips || []).map((c) => {
      const x = entry(c.item_id) || {};
      return { name: c.name, category: x.category_name || '', unit: x.unit_symbol || '', item_id: c.item_id, bmWhy: c.why };
    }),
  };
}

/**
 * The name a row was written with: a scanned row's printed name (the form overwrites item_name and
 * original_ocr_name with the item's name once it is matched), a typed row's typed name.
 */
export function rowPrintedName(row) {
  if (!row) return '';
  if (row.bm_scan_ref) return row.bm_scan_printed || row.original_ocr_name || row.item_name || '';
  return row.bm_printed_name || row.item_name || '';
}

/** The chips a row shows: none for a green row (until "Change"), else the typed option, then the answer's chips. */
export function chipsEntry(row) {
  if (!row || row.bm_colour === 'green') return [];
  return [{ name: row.item_name, category: row.category || '', unit: row.unit || '', isTypedOption: true }, ...(row.smmf_suggestions || [])];
}

const chipIds = (list) => (list || []).map((c) => c && c.item_id).join(',');

/**
 * The vendor changed (picked or tapped): rows the user has not settled are asked again with the
 * new context — typed rows by what was typed, scanned rows by their printed name (as scanned
 * lines, so check 1 applies). Settled rows, and rows whose answer did not change, are returned as
 * the very same objects. Fix 13 (Task 11) row 1: a row that now names another item takes it through
 * enrich(row, inventoryItem) — the form's own exact-match door (category, tax, unit) — and every
 * changed row's chips are put in chips (Map<index, chipsEntry>) for the form to show.
 */
export function rerunUnsettled(rows, bm, { enrich = null, chips = null } = {}) {
  if (!bm || !bm.ctx || !Array.isArray(rows)) return rows;
  return rows.map((row, i) => {
    if (!row || row.bm_settled || row.userConfirmed || !String(row.item_name || '').trim()) return row;
    const scanned = !!row.bm_scan_ref;
    // A scanned row is asked exactly as the server asks it at Save: by the printed name and with the
    // scan's own matcher answer.
    const printed = rowPrintedName(row);
    const line = scanned
      ? ladderLineOf(row, i + 1, { inputKind: 'scanned', printedName: printed, today: row.bm_today || { item_id: null, status: 'none', suggestions: [] } })
      : { ...typedLineOf({ ...row, bm_printed_name: printed }, i + 1), input_kind: 'typed' };
    let d;
    try { d = decideLine(line, bm.ctx); } catch (_) { return row; }
    if (d.colour === 'left_out') return row; // the screen leaves an already-listed line alone
    const m = matchResultOf(d, bm);
    const itemNow = m.itemId == null ? null : Number(m.itemId);
    const itemWas = row.item_id == null || row.item_id === '' ? null : Number(row.item_id);
    if (d.colour === row.bm_colour && itemNow === itemWas && chipIds(m.suggestions) === chipIds(row.smmf_suggestions)) return row;
    const next = {
      ...row,
      bm_colour: d.colour, bm_reason: d.reason, bm_notes: d.notes, bm_preselect: d.preselect, bm_source_check: d.check_no,
      smmf_suggestions: m.suggestions,
      matchStatus: m.status,
      item_id: m.itemId,
      is_existing_item: !!m.itemId,
      item_name: m.inventoryItem ? m.inventoryItem.name : (scanned ? printed : row.item_name),
    };
    if (chips) chips.set(i, chipsEntry(next));
    return enrich && m.status === 'exact' && m.inventoryItem && itemNow !== itemWas ? enrich(next, m.inventoryItem) : next;
  });
}

/** The ladder's answer for a typed row (checks 2–6; 2–3 only with a certain vendor), or null to fall back. */
export function decideTypedRow(row, bm) {
  if (!bm || !bm.ctx || !row) return null;
  const line = typedLineOf(row, 1);
  if (!line.printed_name) return null;
  try {
    return decideLine(line, bm.ctx);
  } catch (_) {
    return null;
  }
}

const ITEM_KEYS = ['bm_chip_rank', 'bm_user_changed', 'bm_action', 'bm_source_check', 'bm_qty_from_memory'];

/** The bm_* fields a stock-in row sends at Save. */
export function buildBmItemFields(row) {
  if (!row || !row.bm_input_kind) return {};
  const out = {};
  if (row.bm_scan_ref) {
    out.bm_scan_ref = row.bm_scan_ref;
    out.bm_line_no = row.bm_line_no;
  }
  out.bm_input_kind = row.bm_input_kind;
  if (!row.bm_scan_ref) out.bm_printed_name = String(row.bm_printed_name || row.item_name || '').trim();
  out.bm_settled = !!(row.bm_settled || row.userConfirmed);
  for (const k of ITEM_KEYS) if (row[k] !== undefined && row[k] !== null) out[k] = row[k];
  return out;
}

/**
 * The row fields to set when the user types an item name. A typed row keeps exactly what was
 * typed; a scanned row keeps its printed name on the server and is marked as changed by the user,
 * so the Save re-check never judges it by the old printed name.
 */
export function nameTypedPatch(row, raw) {
  if (row && row.bm_scan_ref) return { bm_user_changed: true };
  return { bm_input_kind: 'typed', bm_printed_name: raw };
}

/**
 * The form's clean-up of a scanned line nothing matched (moved here from StockInForm unchanged).
 * Its first rule, /^[A-Z]{2,}\s+/i, was meant for brand prefixes ("MTR ") but with the i flag it
 * drops ANY first word ("cucumber (kheera)" → "(kheera)"); on the Bill Memory screen "Add as new
 * ingredient" would create that name and the lesson would keep it, so the rule is skipped there.
 */
export function cleanUnmatchedName(name) {
  let cleaned = String(name || '');
  // Fix 13 (Task 12) row 4: only a leading item code goes ("LAIPL12 Onion", "45-ROCKET"), by the shared
  // core's rule — never a word (the old /^[A-Z]{2,}\s+/ turned "cucumber (kheera)" into "(kheera)").
  const mn = makeMatchName(cleaned);
  if (mn && stripItemCode(mn, cleaned) !== mn) cleaned = cleaned.replace(/^\s*\S+?(\s*-\s*|\s+)/, '');
  // Remove quantity+unit patterns: "100 GR", "1 KG", "500 ML", etc.
  cleaned = cleaned.replace(/\b\d+\s*(KG|KILOGRAM|LITRE|LITER|LTR|L|GRAM|GM|G|ML|MILLILITER|PC|PCS|PIECE)\b/gi, '');
  // Remove box/packet counts: "1X16 BOX", "2X24 PKT", etc.
  cleaned = cleaned.replace(/\b\d+X\d+\s*(BOX|BAG|PKT|PACKET|PACK)\b/gi, '');
  // Remove standalone numbers at the end
  cleaned = cleaned.replace(/\s+\d+\s*$/, '');
  // Remove extra spaces and hyphens
  cleaned = cleaned.replace(/[-/]+/g, ' ').replace(/\s+/g, ' ').trim();
  // Only use cleaned version if it's not empty
  return cleaned.length >= 3 ? cleaned : name;
}

/**
 * A checked green in one line (Fix plan, I3 / I2): "kaju → Cashew · Ramesh Traders · vendor lessons · Learned on
 * 12 Oct · 2 kg at ₹900" — the printed or typed name, the saved item, vendor, source, reason, quantity and rate.
 */
export function checkRowText(r) {
  if (!r) return '';
  const parts = [`${r.printed_name || r.match_name || ''} → ${r.saved_item || '—'}`];
  if (r.vendor_name) parts.push(r.vendor_name);
  if (r.words) parts.push(r.words);
  const why = r.reason && r.reason.code ? reasonText(r.reason.code, r.reason.params) : '';
  if (why) parts.push(why);
  if (r.qty != null) parts.push(`${r.qty}${r.unit ? ` ${r.unit}` : ''}${r.rate != null ? ` at ₹${r.rate}` : ''}`);
  return parts.join(' · ');
}

/** "Last saves: 20 Oct Cashew, 13 Oct Cashew" — the name's last 3 saves; '' when none. */
export function lastSavesText(r) {
  const list = (r && r.last_saves) || [];
  return list.length ? `Last saves: ${list.map((x) => `${shortDate(x.saved_at)} ${x.item || '—'}`).join(', ')}` : '';
}

/** Where the AI name check lives (backend routes/aiValidator.js, mounted at /api/stock-in). */
export const AI_NAME_CHECK_PATH = '/stock-in/ai-validate-comprehensive';

/**
 * Fix 13 (Task 12) row 3: ask the AI name check about one row — only while the no-popup screen is On,
 * the only place its answer is used (one grey "not a kitchen item" line), so it costs nothing anywhere
 * else. → the response, or null when not asked.
 */
export async function aiNameCheck({ screenOn, businessId, index, row, inventory }) {
  if (!screenOn || !row) return null;
  const name = String(row.item_name || '').trim();
  return http.post(AI_NAME_CHECK_PATH, {
    businessId,
    items: [{ index, item_name: name, category: row.category || '', original_ocr_name: row.original_ocr_name || name }],
    all_inventory_items: (inventory || []).map((inv) => ({ item_id: inv.item_id, name: inv.name || inv.item_name, category: inv.category, unit: inv.unit })),
  }, { timeoutMs: 15000 });
}

/** Top-level bm_* fields: scanned lines the user deleted, left-out lines added back. */
export function buildBmTopFields({ deletedLines, addedBack, vendorCertain, vendorId, rows } = {}) {
  const out = {};
  // Fix 13 row 5: a deleted line brought back by Undo / Redo / Revert all is saved, not deleted.
  const back = new Set((rows || []).filter((r) => r && r.bm_scan_ref && r.bm_line_no).map((r) => `${r.bm_scan_ref}|${r.bm_line_no}`));
  const deleted = (Array.isArray(deletedLines) ? deletedLines : []).filter((d) => !(d && back.has(`${d.bm_scan_ref}|${d.bm_line_no}`)));
  if (deleted.length) out.bm_deleted_lines = deleted;
  if (Array.isArray(addedBack) && addedBack.length) out.bm_added_back = addedBack;
  // Only a vendor picked from the dropdown or tapped from the vendor buttons is certain (design §9).
  if (vendorCertain === true && Number(vendorId) > 0) {
    out.bm_vendor_certain = true;
    out.bm_vendor_id = Number(vendorId);
  }
  return out;
}

/** Fix 13 row 4: the old popup's New ingredient / Non-consumable mark the row exactly as the red card does. */
export function redChoiceFields(choice) {
  return { bm_action: choice === 'consumable' ? 'created_new' : 'non_consumable' };
}

/** Fix 13 row 6: the kind of bill a row came from — a spreadsheet, pasted text, else a scanned image or PDF. */
export function inputKindOf({ isExcel = false, isPastedText = false } = {}) {
  if (isExcel) return 'excel';
  if (isPastedText) return 'text';
  return 'scanned';
}

/** The kind a loaded row keeps: the one its file set, else scanned. */
export function rowInputKind(it) {
  return it && ['excel', 'text', 'scanned'].includes(it.bm_input_kind) ? it.bm_input_kind : 'scanned';
}

// ── Fix 12: the learning made visible (plain words; the server sends numbers only) ──

/** The grey line in the save confirmation, from the save response's bill_memory ({ taught, allKnown }); '' for none. */
export function confirmationLine(bm) {
  if (!bm) return '';
  const n = Number(bm.taught) || 0;
  if (n > 0) {
    return n === 1
      ? 'Invexis learned 1 new name from this bill. Next time it will be filled for you.'
      : `Invexis learned ${n} new names from this bill. Next time they will be filled for you.`;
  }
  return bm.allKnown ? 'Every name on this bill was already known.' : '';
}

/**
 * The grey line under the vendor field: from the vendor's 3rd bill (2 saved before this one), only when
 * this bill needs fewer taps than the first did and a source is On. `progress` is GET /vendor-progress;
 * `needNow` the rows on screen still needing a tap.
 */
export function vendorProgressText(progress, needNow, on) {
  if (!on || !progress || !progress.name || progress.firstTaps == null || (Number(progress.bills) || 0) < 2) return '';
  const a = Number(progress.firstTaps);
  const b = Number(needNow) || 0;
  if (!(b < a)) return '';
  return `${progress.name}: your first bill needed ${a} ${a === 1 ? 'tap' : 'taps'}; this one needs ${b}.`;
}

/** The Vendors view's "Memory score" cell: 0.96 → "96%"; none (below 3 bills) → "—". */
export function memoryScoreText(score) {
  return score == null ? '—' : `${Math.round(Number(score) * 100)}%`;
}

export const INTRO_TEXT = 'New: Stock In now learns from your bills. Green rows are filled for you. Tap any row to change it.';
